import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const today = () => new Date().toISOString().split("T")[0];
const tomorrow = () => new Date(Date.now() + 86400000).toISOString().split("T")[0];

export const ManualBookingModal = ({ rooms, onClose, onSaved }) => {
  const available = (rooms || []).filter((r) => r.is_available);
  const [form, setForm] = useState({
    room_id: available[0]?.room_id || "", guest_name: "", guest_phone: "", guest_email: "",
    check_in: today(), check_out: tomorrow(), guests: 2, arrival_time: "", special_requests: "", coupon_code: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const room = rooms.find((r) => r.room_id === form.room_id);
  const nights = Math.max(0, Math.round((new Date(form.check_out) - new Date(form.check_in)) / 86400000));
  const estTotal = room ? Math.round(room.price_per_night * nights * 1.12) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (nights < 1) { toast.error("Check-out must be after check-in"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/bookings", { ...form, guests: Number(form.guests) });
      toast.success(`Booking ${data.booking_ref} added`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add booking");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="manual-booking-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Add Booking</DialogTitle>
          <DialogDescription>Record a walk-in or phone booking. Marked pay-at-hotel; no guest email/SMS is sent.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Room</Label>
            <select value={form.room_id} onChange={(e) => set("room_id", e.target.value)} required data-testid="manual-room-select"
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
              {available.map((r) => <option key={r.room_id} value={r.room_id}>{r.name} — ₹{r.price_per_night}/night</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Check-in</Label>
              <Input type="date" min={today()} value={form.check_in} onChange={(e) => set("check_in", e.target.value)} required data-testid="manual-checkin" /></div>
            <div><Label className="text-xs">Check-out</Label>
              <Input type="date" min={form.check_in} value={form.check_out} onChange={(e) => set("check_out", e.target.value)} required data-testid="manual-checkout" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Guest name</Label>
              <Input required value={form.guest_name} onChange={(e) => set("guest_name", e.target.value)} placeholder="e.g. Rahul Verma" data-testid="manual-guest-name" /></div>
            <div><Label className="text-xs">Phone</Label>
              <Input required value={form.guest_phone} onChange={(e) => set("guest_phone", e.target.value)} placeholder="+91 ..." data-testid="manual-guest-phone" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Email (optional)</Label>
              <Input type="email" value={form.guest_email} onChange={(e) => set("guest_email", e.target.value)} placeholder="you@email.com" data-testid="manual-guest-email" /></div>
            <div><Label className="text-xs">Guests</Label>
              <select value={form.guests} onChange={(e) => set("guests", e.target.value)} data-testid="manual-guests"
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                {[1, 2, 3, 4].map((g) => <option key={g} value={g}>{g} Guest{g > 1 ? "s" : ""}</option>)}
              </select></div>
          </div>
          <div><Label className="text-xs">Special requests (optional)</Label>
            <Textarea rows={2} value={form.special_requests} onChange={(e) => set("special_requests", e.target.value)} data-testid="manual-requests" /></div>
          <div className="rounded-lg bg-muted/50 p-3 text-sm flex justify-between">
            <span className="text-muted-foreground">{nights} night{nights !== 1 ? "s" : ""} · incl. 12% tax</span>
            <span className="font-mono font-bold text-secondary">₹{estTotal}</span>
          </div>
          <Button type="submit" disabled={saving} className="w-full h-11 bg-primary hover:bg-primary/90 gap-2" data-testid="manual-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add Booking
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
